// services/hotelSearchApi.js — FULLY FIXED VERSION
// Supplier uses:
//   GET  /autosuggest?location=X       ✅ confirmed 200
//   GET  /search?locationId=X&...      (GET with query params)
//   POST /details                      (POST with JSON body)
//   POST /booking                      (POST with JSON body)

import { requestSessionExpiry } from "../AuthProvider/AuthContext";


const BASE = (
  import.meta.env?.REACT_APP_API_URL ||
  process.env.REACT_APP_API_URL ||
  "http://localhost:5000"
).replace(/\/+$/, "");

const API_BASE = /\/api$/i.test(BASE)
  ? BASE
  : `${BASE}/api`;


async function apiFetch(path, options = {}, token = null) {
  const headers = {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(options.headers || {}),
  };

  const res = await fetch(`${API_BASE}${path}`, { ...options, headers });

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    if (token && (res.status === 401 || /invalid token/i.test(String(err.error || err.message || "")))) {
      requestSessionExpiry();
    }
    throw Object.assign(new Error(err.error || "API error"), {
      status:  res.status,
      details: err.details,
    });
  }
  return res.json();
}

function getToken() {
  try { return localStorage.getItem("mbt_token") || null; } catch { return null; }
}

export async function initiateHotelEasebuzzPayment({
  booking,
  amount,
  nights,
  token,
  onAuthExpired,
  paymentContext = {},
}) {
  const resolvedNights = nights || booking?.nights || 1;

  const guestName =
    booking?.guestFirstName ||
    booking?.guest_first_name ||
    booking?.guest?.firstName ||
    "Traveller";

  const guestEmail =
    booking?.guestEmail ||
    booking?.guest_email ||
    booking?.guest?.email ||
    "";

  const guestPhone =
    booking?.guestPhone ||
    booking?.guest_phone ||
    booking?.guest?.phone ||
    "";

  const quoteId = paymentContext.quoteId || booking?.quoteId || "";
  const quotedAmount = Number(
    booking?.pricing?.payableTotal ?? amount
  );

  if (!quoteId) {
    throw new Error("Hotel price quote is missing. Please restart checkout.");
  }

  if (!Number.isFinite(quotedAmount) || quotedAmount <= 0) {
    throw new Error("A valid hotel payment amount is required.");
  }

  const res = await fetch(`${API_BASE}/payment/easebuzz/initiate`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({
      service: "hotel",
      quoteId,
      amount: quotedAmount,
      productinfo: `Hotel ${
        booking?.name ||
        booking?.hotel_name ||
        booking?.hotelName ||
        "Booking"
      } ${resolvedNights}N`,
      firstname: guestName,
      email: guestEmail,
      phone: guestPhone,
      bookingReference:
        paymentContext.bookingReference ||
        booking?.requestId ||
        booking?.request_id ||
        booking?.bookingReference ||
        booking?.id,
      udf1: String(
        booking?.id || booking?.hotel_id || booking?.hotelId || ""
      ),
      udf2: String(booking?.checkIn || booking?.check_in || ""),
      udf3: String(booking?.checkOut || booking?.check_out || ""),
      udf4: String(
        booking?.roomSelections
          ?.map((room) => room.roomName)
          .filter(Boolean)
          .join(", ") ||
          booking?.selectedRoom?.name ||
          booking?.rateName ||
          booking?.room_type ||
          "Hotel Room"
      )
        .replace(/[^a-zA-Z0-9 ]/g, " ")
        .replace(/\s+/g, " ")
        .trim()
        .slice(0, 50) || "Hotel Room",
      udf5: String(resolvedNights),
    }),
  });

  if (res.status === 401) {
    onAuthExpired?.();
    throw new Error(
      "Your login session expired. Please log in again to continue payment."
    );
  }

  const data = await res.json().catch(() => ({}));

  if (!res.ok || !data.access_key) {
    throw new Error(
      data.error ||
        data.detail?.error_desc ||
        "Could not initiate payment."
    );
  }

  return data;
}

/**
 * Create an authoritative server-side price quote for a hotel selection.
 * The server re-reads supplier rates and applies the admin's global pricing
 * rules; no money values are sent by the client.
 */

export function createHotelQuote(
  {
    searchKey,
    hotelId,
    supplierHotelId,
    checkIn,
    checkOut,
    requestId,
    roomSelections,
    mealKeys,
    occupancies,
  },
  token = null
) {
  return apiFetch(
    "/hotel-search/quote",
    {
      method: "POST",
      body: JSON.stringify({
        searchKey,
        hotelId,
        supplierHotelId,
        checkIn,
        checkOut,
        requestId,
        roomSelections: roomSelections || [],
        mealKeys: mealKeys || [],
        occupancies: occupancies || [],
      }),
    },
    token || getToken()
  );
}

/**
 * Verify a hotel payment with the backend before anything is marked paid.
 * The backend validates the Easebuzz signature hash when present, otherwise
 * queries Easebuzz's Transaction V2 API, and reconciles the amount.
 */
export async function verifyHotelEasebuzzPayment(payload, token = null) {
  const res = await fetch(`${API_BASE}/payment/easebuzz/hotel/verify`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(payload || {}),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data.error || "Payment verification failed.");
    err.status = res.status;
    err.code = data.code;
    throw err;
  }
  return data; // { received, status, verified, alreadyProcessed }
}

const hotelApi = {

  // ── 1. Autosuggest ──────────────────────────────────────────
  // GET — no auth needed — confirmed working ✅
  autosuggest(location) {
    if (!location?.trim()) return Promise.resolve([]);
    return apiFetch(
      `/hotel-search/autosuggest?location=${encodeURIComponent(location.trim())}`
    );
  },

  // ── 2. Search ───────────────────────────────────────────────
  // GET with query params — occupancies sent as JSON string
  search({ locationId, checkIn, checkOut, occupancies, countryOfResidence, travelType, requestId }) {
    const country = String(countryOfResidence || "India").trim();
    const countryName = country.toLowerCase() === "in" ? "India" : country;

    const params = new URLSearchParams({
      locationId,
      checkIn,
      checkOut,
      countryOfResidence: countryName,
      travelType:         String(travelType ?? 1),
      requestId:          requestId          || `req_${Date.now()}`,
    });
    // Send occupancies as a JSON string in the query
const sanitizedOccupancies = (occupancies || [{ adultCount: 2, childCount: 0, childAges: [] }])
  .map(o => ({ ...o, childAges: Array.isArray(o.childAges) ? o.childAges : [] }));
params.append("occupancies", JSON.stringify(sanitizedOccupancies));
    return apiFetch(
      `/hotel-search/search?${params.toString()}`,
      {},           // GET — no method/body
      getToken()
    );
  },

  // ── 3. Hotel Details ────────────────────────────────────────
  // POST with JSON body
details({ hotelId, searchKey, checkIn, checkOut, occupancies, countryOfResidence, travelType, requestId }) {
  
  // ── Safe date fallbacks ──────────────────────────────────────
  const today = new Date();
  const toDateStr = (d) => d.toISOString().split("T")[0];

  const safeCheckIn = (checkIn && checkIn.length >= 8)
    ? checkIn
    : toDateStr(new Date(today.getTime() + 86400000));     // tomorrow

  const safeCheckOut = (checkOut && checkOut.length >= 8 && checkOut > safeCheckIn)
    ? checkOut
    : toDateStr(new Date(today.getTime() + 2 * 86400000)); // day after tomorrow

  // ── Normalize country ────────────────────────────────────────
  const country = String(countryOfResidence || "India").trim();
  const countryName = country.toLowerCase() === "in" ? "India" : country;

  return apiFetch(
    "/hotel-search/details",
    {
      method: "POST",
      body: JSON.stringify({
        hotelId,
        searchKey:          searchKey    || "",
        checkIn:            safeCheckIn,
        checkInDate:        safeCheckIn,
        checkOut:           safeCheckOut,
        checkOutDate:       safeCheckOut,
occupancies: (occupancies || [{ adultCount: 2, childCount: 0, childAges: [] }])
  .map(o => ({
    ...o,
    title: o.title || "Mr",
    childAges: Array.isArray(o.childAges) ? o.childAges : [],
  })),
        countryOfResidence: countryName,
        travelType:         travelType   ?? 1,
        requestId:          requestId    || `req_${Date.now()}`,
      }),
    },
    getToken()
  );
},

  // ── 4. Create Booking ───────────────────────────────────────
  book({
    requestId,
    searchKey,
    onHold,
    hotelId,
    rateKey,
    quoteId,
    pricing,
    guests,
    countryCode,
    guestContactNumber,
    guestEmail,
    roomAllocation,
    roomDetails,
    roomSelections,
    specialRequests,
    totalAmount,
    paymentMethod,
    hotelName,
    nights,
    checkIn,
    checkOut,
    occupancies,
    destinationCountry,
    paymentStatus,
    travelType,
    countryOfResidence,
    location,
    stars,
    image,
    txnid,
    easepayid,
    gstDetails,
  }) {
    const finalRoomAllocation =
      Array.isArray(roomAllocation) && roomAllocation.length
        ? roomAllocation
        : roomDetails;

    const payableAmount = Number(
      pricing?.payableTotal ?? totalAmount ?? 0
    );

    return apiFetch(
      "/hotel-search/booking",
      {
        method: "POST",
        body: JSON.stringify({
          requestId,
          searchKey,
          onHold: onHold ?? false,
          hotelId,
          rateKey,
          quoteId: quoteId || "",
          pricing: pricing || null,
          guests,
          countryCode: countryCode || "+91",
          guestContactNumber,
          guestEmail,
          roomAllocation: finalRoomAllocation,
          roomDetails: roomDetails || finalRoomAllocation,
          roomSelections,
          specialRequests,
          totalAmount: payableAmount,
          paymentMethod,
          paymentStatus,
          hotelName,
          nights,
          checkIn,
          checkOut,
          occupancies,
          destinationCountry,
          travelType,
          countryOfResidence,
          location,
          stars,
          image,
          txnid,
          easepayid,
          ...(gstDetails?.enabled ? { gstDetails } : {}),
        }),
      },
      getToken()
    );
  },

  // ── 5a. Cancellation Init ───────────────────────────────────
  cancellationInit(bookingId, requestId) {
    return apiFetch(
      "/hotel-search/cancellation/init",
      { method: "POST", body: JSON.stringify({ bookingId, requestId }) },
      getToken()
    );
  },

  // ── 5b. Cancellation Confirm ────────────────────────────────
  cancellationConfirm(bookingId, internalBookingId = null, requestId) {
    return apiFetch(
      "/hotel-search/cancellation/confirm",
      { method: "POST", body: JSON.stringify({ bookingId, internalBookingId, requestId }) },
      getToken()
    );
  },

  // ── 6. Booking Details ──────────────────────────────────────
  bookingDetails(bookingId) {
    return apiFetch(
      `/hotel-search/booking/${encodeURIComponent(bookingId)}`,
      {},
      getToken()
    );
  },

  // ── 7. Booking History ──────────────────────────────────────
  bookingHistory({ fromDate, toDate, bookingId, guestMobile, confirmationNo, page = 1, pageSize = 20 } = {}) {
    const params = new URLSearchParams();
    if (fromDate)       params.set("fromDate",      fromDate);
    if (toDate)         params.set("toDate",         toDate);
    if (bookingId)      params.set("bookingId",      bookingId);
    if (guestMobile)    params.set("guestMobile",    guestMobile);
    if (confirmationNo) params.set("confirmationNo", confirmationNo);
    params.set("page",     String(page));
    params.set("pageSize", String(pageSize));
    return apiFetch(`/hotel-search/booking-history?${params.toString()}`, {}, getToken());
  },

  // ── 8. Hold Confirm ─────────────────────────────────────────
  holdConfirm(bookingId, requestType, requestId, payment = {}) {
    if (!["CONFIRM", "RELEASE"].includes(requestType))
      return Promise.reject(new Error("requestType must be CONFIRM or RELEASE"));
    return apiFetch(
      "/hotel-search/hold-confirm",
      { method: "POST", body: JSON.stringify({ bookingId, requestType, requestId, ...payment }) },
      getToken()
    );
    
  },
   saveSpecialRequests({ hotelId, searchKey, selections }) {
    return apiFetch(
      "/hotel-search/special-requests",
      { method: "POST", body: JSON.stringify({ hotelId, searchKey, selections }) },
      getToken()
    );
  },
  quote(params) {
    return createHotelQuote(params, getToken());
  },
};

export default hotelApi;
